import { useState, lazy, Suspense } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { Plus, Loader2 } from "lucide-react";

const LazyPicker = lazy(() =>
  import("@emoji-mart/react").then((mod) => ({ default: mod.default }))
);

const QUICK_EMOJIS = [
  { native: "\u{1F44D}", id: "thumbsup", name: "Thumbs Up" },
  { native: "\u{2764}\u{FE0F}", id: "heart", name: "Heart" },
  { native: "\u{1F602}", id: "joy", name: "Face with Tears of Joy" },
  { native: "\u{1F525}", id: "fire", name: "Fire" },
  { native: "\u{1F62E}", id: "open_mouth", name: "Surprised" },
  { native: "\u{1F622}", id: "cry", name: "Crying" },
] as const;

type EmojiPickerProps = {
  onSelect: (emoji: string) => void;
  onClose: () => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
};

export function EmojiPicker({
  onSelect,
  onClose,
  open,
  onOpenChange,
  children,
}: EmojiPickerProps) {
  const [showFullGrid, setShowFullGrid] = useState(false);

  const handleQuickSelect = (emoji: string) => {
    onSelect(emoji);
    onClose();
    setShowFullGrid(false);
  };

  const handleFullSelect = (emojiData: { native: string }) => {
    onSelect(emojiData.native);
    onClose();
    setShowFullGrid(false);
  };

  const handleOpenChange = (v: boolean) => {
    onOpenChange(v);
    if (!v) {
      setShowFullGrid(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        side="top"
        align="start"
        className={cn("p-2", showFullGrid ? "w-[352px]" : "w-auto")}
        onPointerDownOutside={(e) => {
          e.preventDefault();
          handleOpenChange(false);
        }}
      >
        {showFullGrid ? (
          <Suspense
            fallback={
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            }
          >
            <FullEmojiGrid onSelect={handleFullSelect} />
          </Suspense>
        ) : (
          <div className="flex items-center gap-1">
            {QUICK_EMOJIS.map((emoji) => (
              <button
                key={emoji.id}
                type="button"
                onClick={() => handleQuickSelect(emoji.native)}
                className="h-9 w-9 flex items-center justify-center rounded-full hover:bg-muted transition-colors text-lg"
                aria-label={emoji.name}
              >
                {emoji.native}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setShowFullGrid(true)}
              className="h-9 w-9 flex items-center justify-center rounded-full hover:bg-muted transition-colors text-muted-foreground"
              aria-label="More emojis"
            >
              <Plus className="h-4 w-4" />
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

function FullEmojiGrid({
  onSelect,
}: {
  onSelect: (emojiData: { native: string }) => void;
}) {
  return (
    <LazyPicker
      onEmojiSelect={onSelect}
      theme="light"
      set="native"
      skinTonePosition="none"
      previewPosition="none"
      maxFrequentRows={1}
      perLine={8}
    />
  );
}
